import { AccountDates } from '@vendasta/accounts/legacy';
import { App, AppType } from '@vendasta/marketplace-apps';
import { addDays, differenceInDays, isBefore, isValid, subDays } from 'date-fns';
import { productCanSso } from 'marketplace-ui';

export interface SimpleProduct {
  productId: string;
  editionId: string;
  quantity: number;
  isTrial: boolean;
  trialEnded?: boolean;
  trialExpirationDate?: Date;
  trialDaysRemaining?: number;
  name?: string;
  iconUrl?: string;
  addons?: SimpleProduct[];
  activationDate?: Date;
  commitmentDate?: Date;
  renewalDate?: Date;
  entryUrl?: string;
}

export class SimpleApp extends App {
  constructor(app: App) {
    super(app);
  }

  public isAddon(): boolean {
    return this?.appType === AppType.APP_TYPE_ADDON;
  }

  public getParentId(): string {
    return this?.parentRequirements?.parentDetails?.key?.appId ?? '';
  }

  public getFullName(): string {
    const name = this?.sharedMarketingInformation?.name ?? '';
    const editionName = this?.sharedMarketingInformation?.editionName ?? '';
    return editionName ? `${name} | ${editionName}` : name;
  }

  public getIconUrl(): string {
    return this?.sharedMarketingInformation?.iconUrl ?? '';
  }

  public getEntryUrl(productId: string): string {
    if (this.ssoIntegration?.enabled && productCanSso(productId)) {
      return this.ssoIntegration.entryUrl ?? '';
    }
    return '';
  }
}

export class SimpleAccountDate extends AccountDates {
  trialExpiration: Date | undefined;

  constructor(date: AccountDates) {
    super(date);

    if ('trialExpiration' in date) {
      const expiration = date['trialExpiration'];
      if (typeof expiration === 'string' || expiration instanceof Date) {
        this.trialExpiration = new Date(expiration);
      }
    }
  }

  public getActivationDate(): Date | undefined {
    return this.activation;
  }

  public getRenewalDate(): Date | undefined {
    const date = this.anniversaryDate;
    if (!date || (isValid(date) && isBefore(date, new Date()))) {
      return undefined;
    }
    if (date.getUTCDate() === date.getDate() && -date.getTimezoneOffset() < 1) {
      return subDays(date, 1);
    }
    return date;
  }

  public getCommitmentDate(): Date | undefined {
    if (this.anniversaryDate && this.commitmentDate) {
      if (this.anniversaryDate.getTime() < this.commitmentDate.getTime()) {
        return subDays(this.commitmentDate, 1);
      }
    }
    return undefined;
  }

  public isTrialExpired(): boolean {
    if (this.trialExpiration) {
      return isBefore(this.trialExpiration, new Date());
    }
    return false;
  }

  public getTrialDaysRemaining(): number {
    if (this.trialExpiration) {
      const dayDiff = differenceInDays(addDays(this.trialExpiration, 1), new Date());
      return Math.max(0, dayDiff);
    }
    return 0;
  }

  public getTrialExpirationDate(): Date | undefined {
    return this.trialExpiration;
  }
}
